import FareFamilyMobileDrawer from '@/src/components/mobile/srp/SRPFareFamily/FareFamilyMobileDrawer'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'
import useClevertapForSrp from '@/src/hooks/analytics/clevertap/srp'
import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { getSelectedCurrency } from '@/src/redux/slices/currencySlice'
import { useAppSelector } from '@/src/redux/store'
import { FlightCard, JsonsData, Sectors } from '@/src/types/flight'
import { transformFareFamily } from '@/src/utils/srp/searchUtils/transformFareFamily'
import { useTranslations } from 'next-intl'
import React, { useMemo, useState } from 'react'
import FareFamilyDesktop from './fareFamily'
import { FareFamilyApiResponse, FarePlanState, Plan } from './types'

const FareFamilyRenderController = ({
    flight,
    sectors,
    handleClose,
    jsons,
    handleCreateBooking,
    farePlanState,
    setFarePlanState,
    desktop,
    rawFareFamilyData
}: {
    flight: FlightCard
    sectors: Sectors
    handleClose: () => void
    jsons: JsonsData
    handleCreateBooking: (flight: FlightCard, fareFamilyData: Plan | null) => Promise<void>
    farePlanState: FarePlanState
    setFarePlanState: React.Dispatch<React.SetStateAction<FarePlanState>>
    desktop?: boolean
    rawFareFamilyData?: FareFamilyApiResponse // Raw API response
}) => {
    const { formatCurrency } = useFormatCurrencyForSrp()
    const { formatDate } = useFormatDate()
    const translations = useTranslations()
    const { triggerSrpEvent } = useClevertapForSrp()
    const selectedCurrency = useAppSelector(getSelectedCurrency)
    const { isPerPaxPricingEnabled } = useSearchResults()

    // Transform raw data whenever component mounts or currency changes
    const plans = useMemo(() => {
        if (!rawFareFamilyData?.responseBody?.data?.solutions) {
            return []
        }
        const { plans } = transformFareFamily({
            solutions: rawFareFamilyData.responseBody.data.solutions,
            formatCurrency,
            formatDate,
            translations,
            flight,
            isPerPaxPricingEnabled
        })

        return plans
    }, [rawFareFamilyData, formatCurrency, formatDate, translations, selectedCurrency])
    const getAllSectorsForFamily = (flight: FlightCard | null, sectors: Sectors) => {
        return flight?.sectorKeys?.map(sectorKey => {
            return sectors[sectorKey]
        })
    }

    const isLoadingData = !rawFareFamilyData
    const fareFamilyError = null
    const allSectors = getAllSectorsForFamily(flight, sectors)

    return desktop ? (
        <ErrorBoundaryWrapper identifier="fare-family-desktop">
            <FareFamilyDesktop
                plans={plans}
                loading={isLoadingData}
                error={fareFamilyError}
                fareFamilySectors={allSectors}
                handleClose={handleClose}
                handleFareFamilyBookClick={(selectedPlan: Plan) => {
                    triggerSrpEvent('Air_UI_Action', {
                        action_name: 'fare_families_selected',
                        action_type: 'BUTTON',
                        action_value: selectedPlan.title || 'Value',
                        flightNumber: flight.airlineCodes?.join('-') || 'N/A',
                        airline: flight.airlineCodes?.[0] || 'N/A'
                    })
                    handleCreateBooking(flight, selectedPlan)
                }}
                farePlanState={farePlanState}
                setFarePlanState={setFarePlanState}
                flight={flight}
            />
        </ErrorBoundaryWrapper>
    ) : (
        <ErrorBoundaryWrapper identifier="fare-family-PWA">
            <FareFamilyMobileDrawer
                plans={plans}
                loading={isLoadingData}
                error={fareFamilyError}
                fareFamilySectors={allSectors ?? []}
                handleClose={handleClose}
                handleFareFamilyBookClick={selectedPlan => {
                    triggerSrpEvent('Air_UI_Action', {
                        action_name: 'fare_families_selected',
                        action_type: 'BUTTON',
                        action_value: selectedPlan.title || 'Value',
                        flightNumber: flight.airlineCodes?.join('-') || 'N/A',
                        airline: flight.airlineCodes?.[0] || 'N/A'
                    })
                    handleCreateBooking(flight, selectedPlan)
                }}
                farePlanState={farePlanState}
                setFarePlanState={setFarePlanState}
                jsons={jsons}
                flight={flight}
            />
        </ErrorBoundaryWrapper>
    )
}

const FareFamilyWrapper = ({
    flight,
    sectors,
    handleClose,
    jsons,
    handleCreateBooking,
    desktop,
    rawFareFamilyData
}: {
    flight?: FlightCard
    sectors: Sectors
    handleClose: () => void
    jsons: JsonsData
    handleCreateBooking: (flight: FlightCard, fareFamilyData: Plan | null) => Promise<void>
    desktop?: boolean
    rawFareFamilyData?: FareFamilyApiResponse // Raw API response
}) => {
    const [farePlanState, setFarePlanState] = useState<FarePlanState>({
        selectedFilters: [],
        selectedFlightIndex: 0,
        selectedPlanFareKey: null,
        filteredPlans: []
    })

    if (!flight) {
        return null
    }

    return (
        <FareFamilyRenderController
            flight={flight}
            sectors={sectors}
            handleClose={handleClose}
            jsons={jsons}
            handleCreateBooking={handleCreateBooking}
            farePlanState={farePlanState}
            setFarePlanState={setFarePlanState}
            desktop={desktop}
            rawFareFamilyData={rawFareFamilyData}
        />
    )
}

export default FareFamilyWrapper
